import type { ReactNode } from "react";

interface StructuredDescriptionProps {
  readonly text: string;
  readonly className?: string;
}

type DescriptionBlock =
  | { readonly type: "heading"; readonly text: string }
  | { readonly type: "paragraph"; readonly text: string }
  | { readonly type: "list"; readonly items: readonly string[] };

export function StructuredDescription({ text, className }: StructuredDescriptionProps) {
  const blocks = parseDescription(text);

  return (
    <div className={className}>
      {blocks.map((block, index) => renderBlock(block, index))}
    </div>
  );
}

function renderBlock(block: DescriptionBlock, index: number): ReactNode {
  if (block.type === "heading") {
    return (
      <h3 key={`${block.type}-${index}`} className="mt-5 text-sm font-semibold text-foreground first:mt-0">
        {block.text}
      </h3>
    );
  }

  if (block.type === "list") {
    return (
      <ul
        key={`${block.type}-${index}`}
        className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-foreground marker:text-muted-foreground"
      >
        {block.items.map((item, itemIndex) => (
          <li key={`${item}-${itemIndex}`}>{item}</li>
        ))}
      </ul>
    );
  }

  return (
    <p
      key={`${block.type}-${index}`}
      className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground first:mt-0"
    >
      {block.text}
    </p>
  );
}

/**
 * Ingested supplier text often contains literal escape sequences (`\n`, `\r`,
 * `\t` as two characters rather than control characters). Normalize them to
 * real whitespace before block parsing — the same cleaning the pipeline's
 * report tooling applies in `_clean_text`
 * (product-ingest-processor-tools/scripts/prepare_snoonu_csv_datasets.py).
 */
function normalizeEscapedWhitespace(text: string): string {
  return text
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, " ")
    .replace(/\\r/g, "\n")
    .replace(/\n{3,}/g, "\n\n");
}

function parseDescription(text: string): DescriptionBlock[] {
  const blocks: DescriptionBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) blocks.push({ type: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = () => {
    if (list.length > 0) blocks.push({ type: "list", items: list });
    list = [];
  };

  for (const rawLine of normalizeEscapedWhitespace(text).split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = headingText(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: "heading", text: heading });
      continue;
    }

    const bullet = bulletText(line);
    if (bullet) {
      flushParagraph();
      list.push(bullet);
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return blocks;
}

function headingText(line: string): string | null {
  const markdownHeading = line.match(/^#{1,6}\s+(.+)$/);
  if (markdownHeading) return markdownHeading[1].trim();

  if (line.length <= 80 && line.endsWith(":") && !line.includes("://")) {
    return line.slice(0, -1).trim();
  }

  return null;
}

function bulletText(line: string): string | null {
  const bullet = line.match(/^(?:[-*•‣▪◦●○✓✔]|\d+[.)])\s*(.+)$/);
  return bullet?.[1]?.trim() || null;
}
